import dayjs from "dayjs"
import isBetween from "dayjs/plugin/isBetween"

dayjs.extend(isBetween)

const isWithinSeason = (day, ranges) =>
  ranges.some((range) => {
    const start = dayjs(range.start).startOf("day")
    const end = dayjs(range.end).endOf("day")
    return start.isValid() && end.isValid() && day.isBetween(start, end, null, "[]")
  })

/**
 * A multi-day booking occupies `duration_value` consecutive calendar days from the
 * picked start date, but the date picker only tests that START date against
 * `available_date_ranges` — so a trip booked near the end of a season silently runs
 * past it (e.g. a 2-day trip started Sep 30 of a Sep 1–30 season ends Oct 1).
 *
 * Returns the details needed to warn about that tail, or null when the whole span
 * stays in season / the check doesn't apply. Advisory only — never blocks booking.
 */
export const getSeasonalSpanWarning = (experienceDetail, startDate) => {
  if (experienceDetail?.duration_type !== "multi_day") return null

  const days = Number(experienceDetail?.duration_value)
  if (!Number.isFinite(days) || days <= 1) return null

  const ranges = experienceDetail?.available_date_ranges
  if (!Array.isArray(ranges) || ranges.length === 0) return null

  const start = dayjs(startDate).startOf("day")
  if (!start.isValid()) return null

  // An out-of-season start date is already blocked by the picker's disabledDate, so
  // it isn't this warning's business — only the days the picker never looked at are.
  if (!isWithinSeason(start, ranges)) return null

  // Tested against every range, not just the one holding the start date: adjacent
  // seasons (Sep 1–30 + Oct 1–31) mean a spanning trip can still be fully in season.
  const outOfSeasonDates = Array.from({ length: days - 1 }, (_, offset) => start.add(offset + 1, "day")).filter(
    (day) => !isWithinSeason(day, ranges)
  )
  if (outOfSeasonDates.length === 0) return null

  // The end of the season the trip starts in — the boundary it crosses.
  const seasonEnd = ranges
    .map((range) => dayjs(range.end).startOf("day"))
    .filter((end) => end.isValid() && !end.isBefore(start))
    .sort((a, b) => a.valueOf() - b.valueOf())[0]

  return {
    days,
    tripStartDate: start,
    tripEndDate: start.add(days - 1, "day"),
    outOfSeasonDates,
    seasonEnd: seasonEnd ?? null
  }
}
